/** Project-only navigation state.
 *
 * `currentProject` is the sole persisted and selectable scope. Legacy
 * space-shaped fields are temporary in-memory adapters for presentation
 * components; they never fetch or mutate Space resources.
 */
import { create } from "zustand";
import {
  mirroredStore,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
import type { Project, Space } from "@/api/spaces";
import * as projectsApi from "@/api/spaces";
import { isTauri } from "@/lib/is-tauri";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";
import { desktopApiOrigin } from "@/lib/release-channel";

const LS_PROJECT_KEY = "denker:currentProjectId";
const LEGACY_LS_SPACE_KEY = "denker:currentSpaceId";
const LS_SELECTION_USER_KEY = "denker:selectionUserId";
const DESKTOP_API_URL = desktopApiOrigin();
let nextProjectFitRequestId = Date.now();

async function tauriInvoke<T>(
  cmd: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, { apiUrl: DESKTOP_API_URL, ...args });
}

interface ProjectsSnapshot {
  projects: Project[];
}

function legacyProjectScope(project: Project): Space {
  return {
    id: project.id,
    project: project.id,
    name: project.name,
    ticket_prefix: project.ticket_prefix ?? "T",
    canvas_state: project.canvas_state ?? {},
    color: project.color,
    is_pinned: project.is_pinned,
    is_onboarding: false,
    sort_order: project.sort_order,
    created_at: project.created_at,
    updated_at: project.updated_at,
  };
}

function legacyScopeMap(projects: Project[]): Record<string, Space[]> {
  return Object.fromEntries(
    projects.map((project) => [project.id, [legacyProjectScope(project)]]),
  );
}

async function listProjects(forceRefresh = false): Promise<Project[]> {
  if (!isTauri()) return projectsApi.listProjects();
  if (forceRefresh) await tauriInvoke<void>("invalidate_projects_cache");
  await tauriInvoke<void>("ensure_projects_fresh");
  return (await tauriInvoke<ProjectsSnapshot>("get_projects")).projects;
}

async function createProject(name: string): Promise<Project> {
  if (!isTauri()) return projectsApi.createProject({ name });
  return tauriInvoke<Project>("create_project", { name });
}

async function archiveProject(projectId: string): Promise<void> {
  if (!isTauri()) return projectsApi.archiveProject(projectId);
  await tauriInvoke<void>("delete_project", { projectId });
}

type ProjectPatch = Partial<{
  name: string;
  description: string;
  color: string;
  is_pinned: boolean;
  sort_order: number;
  parallel_sessions_enabled: boolean;
}>;

async function updateProject(
  projectId: string,
  patch: ProjectPatch,
): Promise<void> {
  if (!isTauri()) {
    await projectsApi.updateProject(projectId, patch);
  } else if (typeof patch.name === "string") {
    await tauriInvoke<void>("rename_project", { projectId, name: patch.name });
  } else if (typeof patch.color === "string") {
    await tauriInvoke<void>("update_project_color", {
      projectId,
      color: patch.color,
    });
  } else if (typeof patch.is_pinned === "boolean") {
    await tauriInvoke<void>("toggle_project_pinned", {
      projectId,
      isPinned: patch.is_pinned,
    });
  } else {
    // No cache command for this field: PATCH directly, then retire the
    // shared Rust cache so its next read does not serve the old value.
    await projectsApi.updateProject(projectId, patch);
    await tauriInvoke<void>("invalidate_projects_cache");
  }
}

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  selectionRevision: number;
  isLoading: boolean;
  error: string | null;
  expandedProjectIds: Set<string>;
  pendingProjectFitRequest: { id: number; projectId: string } | null;
  fetchProjects: () => Promise<void>;
  fetchAllProjects: (forceRefresh?: boolean) => Promise<void>;
  setCurrentProject: (project: Project) => void;
  selectProject: (project: Project, options?: { fitOnLoad?: boolean }) => void;
  consumeProjectFitRequest: (id: number) => void;
  toggleProjectExpanded: (projectId: string) => void;
  renameProject: (projectId: string, name: string) => Promise<void>;
  updateProjectColor: (projectId: string, color: string) => Promise<void>;
  toggleProjectPinned: (projectId: string) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  createProjectWithDefaults: (name?: string) => Promise<Project>;
  clear: (options?: { preserveSelectionForUser?: string }) => void;
  adoptSelectionOwner: (userId: string) => void;

  /** @deprecated Project-shaped compatibility view; never backed by Spaces. */
  spaces: Space[];
  /** @deprecated Use currentProject. */
  currentSpace: Space | null;
  /** @deprecated One project-shaped entry per project. */
  allProjectSpaces: Record<string, Space[]>;
  /** @deprecated Use pendingProjectFitRequest. */
  pendingSpaceFitRequest: { id: number; spaceId: string } | null;
  /** @deprecated Use fetchAllProjects. */
  fetchAllProjectsWithSpaces: (forceRefresh?: boolean) => Promise<void>;
  /** @deprecated Selects the represented project. */
  setCurrentSpace: (scope: Space | null) => void;
  /** @deprecated Use selectProject. */
  selectProjectSpace: (
    project: Project,
    scope: Space,
    options?: { fitOnLoad?: boolean },
  ) => void;
  /** @deprecated Use consumeProjectFitRequest. */
  consumeSpaceFitRequest: (id: number) => void;
  /** @deprecated Renames a project. */
  renameSpace: (
    projectId: string,
    scopeId: string,
    name: string,
  ) => Promise<void>;
  /** @deprecated Updates project color. */
  updateSpaceColor: (
    projectId: string,
    scopeId: string,
    color: string,
  ) => Promise<void>;
  /** @deprecated Toggles project pinning. */
  toggleSpacePinned: (projectId: string, scopeId: string) => Promise<void>;
}

export const useProjectStore = create<ProjectState>()(
  mirroredStore("project", (set, get) => {
    let generation = 0;
    let latestProjectsRequest = 0;
    let appliedProjectsRequest = 0;
    const applyProjects = (
      projects: Project[],
      { selectionAtStart }: { selectionAtStart?: string | null } = {},
    ) => {
      const [firstProject] = projects;
      // A signed-in account always has Home: an empty list is a transient
      // read of a cache another window's refresh just reset.
      if (!firstProject) {
        set({ isLoading: false });
        return;
      }
      const listKeys = {
        projects,
        allProjectSpaces: legacyScopeMap(projects),
        isLoading: false,
        error: null,
      };
      const currentId = get().currentProject?.id ?? null;
      // Never rewrite a valid selection: this window's view may be stale and
      // re-emitting it would override a newer selection in every window. A
      // selection made while the request was in flight is newer than its
      // response.
      if (
        projects.some((project) => project.id === currentId) ||
        (selectionAtStart !== undefined && selectionAtStart !== currentId)
      ) {
        set(listKeys);
        return;
      }
      const savedId = localStorage.getItem(LS_PROJECT_KEY);
      const selected =
        projects.find((project) => project.id === savedId) ??
        projects.find((project) => project.is_home) ??
        firstProject;
      localStorage.setItem(LS_PROJECT_KEY, selected.id);
      localStorage.removeItem(LEGACY_LS_SPACE_KEY);
      const currentSpace = legacyProjectScope(selected);
      set({
        ...listKeys,
        currentProject: selected,
        currentSpace,
        spaces: [currentSpace],
        expandedProjectIds: new Set([...get().expandedProjectIds, selected.id]),
      });
    };

    const fetchAllProjects = async (forceRefresh = false) => {
      const requestGeneration = generation;
      const requestId = ++latestProjectsRequest;
      const selectionAtStart = get().currentProject?.id ?? null;
      set({ isLoading: true, error: null });
      try {
        const projects = await listProjects(forceRefresh);
        if (requestGeneration !== generation) return;
        // Never apply a list older than one already applied; a caller awaiting
        // its own fetch still sees at least its own response.
        if (requestId < appliedProjectsRequest) return;
        appliedProjectsRequest = requestId;
        applyProjects(projects, { selectionAtStart });
      } catch {
        if (requestGeneration !== generation) return;
        if (requestId !== latestProjectsRequest) return;
        set({
          error: translate(storesNoticesMessages, "projectsLoadFailed"),
          isLoading: false,
        });
      }
    };

    const selectProject = (
      project: Project,
      options?: { fitOnLoad?: boolean },
    ) => {
      const scope = legacyProjectScope(project);
      localStorage.setItem(LS_PROJECT_KEY, project.id);
      localStorage.removeItem(LEGACY_LS_SPACE_KEY);
      const request = options?.fitOnLoad
        ? { id: nextProjectFitRequestId++, projectId: project.id }
        : null;
      set({
        currentProject: project,
        selectionRevision: get().selectionRevision + 1,
        currentSpace: scope,
        spaces: [scope],
        pendingProjectFitRequest: request,
        pendingSpaceFitRequest: request
          ? { id: request.id, spaceId: request.projectId }
          : null,
      });
    };

    const patchProject = async (projectId: string, patch: ProjectPatch) => {
      const requestGeneration = generation;
      const projects = get().projects.map((project) =>
        project.id === projectId ? { ...project, ...patch } : project,
      );
      set({ projects, allProjectSpaces: legacyScopeMap(projects) });
      try {
        await updateProject(projectId, patch);
      } catch {
        if (requestGeneration === generation) await fetchAllProjects(true);
      }
    };

    return {
      projects: [],
      currentProject: null,
      selectionRevision: 0,
      isLoading: false,
      error: null,
      expandedProjectIds: new Set<string>(),
      pendingProjectFitRequest: null,
      spaces: [],
      currentSpace: null,
      allProjectSpaces: {},
      pendingSpaceFitRequest: null,
      fetchProjects: fetchAllProjects,
      fetchAllProjects,
      setCurrentProject: (project) => selectProject(project),
      selectProject,
      consumeProjectFitRequest: (id) =>
        set((state) =>
          state.pendingProjectFitRequest?.id === id
            ? { pendingProjectFitRequest: null, pendingSpaceFitRequest: null }
            : {},
        ),
      toggleProjectExpanded: (projectId) =>
        set((state) => {
          const next = new Set(state.expandedProjectIds);
          if (next.has(projectId)) next.delete(projectId);
          else next.add(projectId);
          return { expandedProjectIds: next };
        }),
      renameProject: (projectId, name) => patchProject(projectId, { name }),
      updateProjectColor: (projectId, color) =>
        patchProject(projectId, { color }),
      toggleProjectPinned: async (projectId) => {
        const project = get().projects.find((item) => item.id === projectId);
        if (project)
          await patchProject(projectId, { is_pinned: !project.is_pinned });
      },
      deleteProject: async (projectId) => {
        const requestGeneration = generation;
        const state = get();
        if (state.projects.find((project) => project.id === projectId)?.is_home)
          return;
        try {
          if (state.projects.length <= 1)
            await createProject(
              translate(storesNoticesMessages, "defaultProject"),
            );
          if (requestGeneration !== generation) return;
          await archiveProject(projectId);
          if (requestGeneration === generation) await fetchAllProjects(true);
        } catch {
          if (requestGeneration === generation) await fetchAllProjects(true);
        }
      },
      createProjectWithDefaults: async (name) => {
        const requestGeneration = generation;
        const baseName =
          name || translate(storesNoticesMessages, "untitledProject");
        const existingNames = new Set(
          get().projects.map((project) => project.name),
        );
        let finalName = baseName;
        let suffix = 2;
        while (existingNames.has(finalName))
          finalName = `${baseName} ${suffix++}`;
        const project = await createProject(finalName);
        if (requestGeneration !== generation)
          throw new Error(
            "Project creation belongs to a retired account session",
          );
        const projects = get().projects.some((item) => item.id === project.id)
          ? get().projects.map((item) =>
              item.id === project.id ? project : item,
            )
          : [...get().projects, project];
        applyProjects(projects);
        return project;
      },
      clear: (options) => {
        generation += 1;
        const preserveSelection =
          options?.preserveSelectionForUser &&
          localStorage.getItem(LS_SELECTION_USER_KEY) ===
            options.preserveSelectionForUser;
        if (!preserveSelection) {
          localStorage.removeItem(LS_PROJECT_KEY);
          localStorage.removeItem(LEGACY_LS_SPACE_KEY);
          localStorage.removeItem(LS_SELECTION_USER_KEY);
        }
        set({
          projects: [],
          currentProject: null,
          selectionRevision: get().selectionRevision + 1,
          currentSpace: null,
          spaces: [],
          allProjectSpaces: {},
          isLoading: false,
          error: null,
          expandedProjectIds: new Set<string>(),
          pendingProjectFitRequest: null,
          pendingSpaceFitRequest: null,
        });
      },
      adoptSelectionOwner: (userId) => {
        const owner = localStorage.getItem(LS_SELECTION_USER_KEY);
        if (owner && owner !== userId) get().clear();
        localStorage.setItem(LS_SELECTION_USER_KEY, userId);
      },
      fetchAllProjectsWithSpaces: fetchAllProjects,
      setCurrentSpace: (scope) => {
        if (!scope) return;
        const project = get().projects.find(
          (item) => item.id === scope.project || item.id === scope.id,
        );
        if (project) selectProject(project);
      },
      selectProjectSpace: (project, _scope, options) =>
        selectProject(project, options),
      consumeSpaceFitRequest: (id) =>
        set((state) =>
          state.pendingSpaceFitRequest?.id === id
            ? { pendingProjectFitRequest: null, pendingSpaceFitRequest: null }
            : {},
        ),
      renameSpace: (projectId, _scopeId, name) =>
        get().renameProject(projectId, name),
      updateSpaceColor: (projectId, _scopeId, color) =>
        get().updateProjectColor(projectId, color),
      toggleSpacePinned: (projectId) => get().toggleProjectPinned(projectId),
    };
  }),
);

// Selected-project details follow the list in every window. Local only: each
// window receives the list itself, and emitting would resend this window's
// possibly stale selection. Unchanged content keeps currentProject's identity.
// Write `projects` with a plain partial while emits are on, never a function
// updater: derivePatch would ship this local write along with it.
useProjectStore.subscribe((state, prev) => {
  const current = state.currentProject;
  if (state.projects === prev.projects || !current) return;
  const fresh = state.projects.find((project) => project.id === current.id);
  if (!fresh || JSON.stringify(fresh) === JSON.stringify(current)) return;
  const scope = legacyProjectScope(fresh);
  withMutationEmitSuppressed(() =>
    useProjectStore.setState({
      currentProject: fresh,
      currentSpace: scope,
      spaces: [scope],
    }),
  );
});

/**
 * Show `project` in this window alone (a frame pinned to the desktop): its
 * frames save into it, while the selection the other windows share and
 * remember stays as it is. It is this window's only project, so a board
 * here shows that project's tasks alone.
 */
export function showProjectInThisWindow(project: Project): void {
  const scope = legacyProjectScope(project);
  withMutationEmitSuppressed(() =>
    useProjectStore.setState({
      projects: [project],
      currentProject: project,
      currentSpace: scope,
      spaces: [scope],
    }),
  );
}
